<template>
  <div class="shipment-rate-shop-tab" :class="tab.key">
    <RateShopBenchmarkRow
      v-model:freight-allowance="freightAllowanceInternal"
      :show-freight-allowance="shouldShowFreightAllowance"
      :freight-allowance-editable="isFreightAllowanceEditable"
    />

    <Message
      v-if="acceptedRateQuote && !isRateQuoteDispatchable(acceptedRateQuote)"
      severity="error"
      class="rygen-message"
    >
      {{
        t('rateShop.doNotDispatch.entityNameTenderingDisabledMessage', {
          entityName:
            acceptedRateQuote.source === 'BROKERAGE'
              ? lowerCase(t('brokerages.brokers'))
              : lowerCase(t('carriers.carriers')),
        })
      }}
    </Message>

    <Message
      v-if="acceptedRateQuote && rateShop.data?.requiresReRate"
      severity="warn"
      class="rygen-message"
    >
      {{ t('rateShop.staleRateMessage') }}
    </Message>

    <SelectedRateCard
      :rate-quote="acceptedRateQuote"
      :broker-setting="brokerSetting"
      editable
      @edit-brokerage-quote="emit('edit-brokerage-quote', $event)"
      @edit-spot-quote="emit('edit-quote', $event)"
      @edit-quote="emit('edit-quote', $event)"
    />
  </div>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRef, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { lowerCase } from '@rygen/rygen-ui/i18n';
import {
  CapacityProviderType,
  CarrierSetting,
  Loadable,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  useDoNotDispatch,
  useFreightAllowance,
  useRateShop,
} from '@/composables';
import { FreightAllowance, RateQuote, RateShop, Shipment } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import SelectedRateCard from '@/components/carrier/SelectedRateCard.vue';
import RateShopBenchmarkRow from '@/components/rateShop/RateShopBenchmarkRow.vue';
import { ShipmentRateShopTab } from '@/components/shipment/rateShop/ShipmentRateShopHeader.vue';
import Message from 'primevue/message';

const props = defineProps({
  tab: {
    type: Object as PropType<ShipmentRateShopTab>,
    required: true,
  },
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    default: () => ({ loading: false }),
  },
  freightAllowance: {
    type: Object as PropType<FreightAllowance | undefined>,
    default: () => undefined,
  },
});
const { tab, shipment, rateShop, freightAllowance } = toRefs(props);
const emit = defineEmits<{
  (event: 'edit-brokerage-quote', rateQuote: RateQuote): void;
  (event: 'edit-quote', rateQuote: RateQuote): void;
  (
    event: 'update:freight-allowance',
    freightAllowance?: FreightAllowance,
  ): void;
}>();

const { isRateQuoteDispatchable } = useDoNotDispatch();
const {
  freightAllowanceInternal,
  isFreightAllowanceEditable,
  shouldShowFreightAllowance,
} = useFreightAllowance(freightAllowance, emit);
const { t } = useI18n();
const { owningClient } = useParentEntityStore();
const { acceptedRateQuote } = useRateShop(rateShop, {
  acceptedRateQuoteId: toRef(() => shipment.value.acceptedRateQuoteId),
});

const brokerSetting: Ref<CarrierSetting | undefined> = ref();

// The client and the accepted quote both trigger this, so a slow response for
// an earlier broker must not land on top of a newer one.
let requestedScac: string | undefined;

const loadAcceptedBrokerSettings = async (scac?: string) => {
  requestedScac = scac;

  if (!scac || !authorization.hasAuthority('client', 'read')) {
    brokerSetting.value = undefined;
    return;
  }

  try {
    const setting = await api.broker.findBrokerSettingsForScac(scac);

    if (requestedScac === scac) {
      brokerSetting.value = setting;
    }
  } catch (e) {
    if (axios.isCancel(e) || requestedScac !== scac) {
      return;
    }

    brokerSetting.value = undefined;

    if (axios.isAxiosError(e) && e.response?.status === 404) {
      // The accepted quote's carrier may not be a configured broker at all,
      // which is a normal miss rather than an error worth surfacing.
      return;
    }

    console.error("Error loading accepted broker's settings", e);
  }
};

// Only a brokered quote has broker settings to resolve, so a direct carrier
// rate never pays for the lookup.
const brokeredQuoteScac = computed(() =>
  acceptedRateQuote.value?.source === 'BROKERAGE' ||
  acceptedRateQuote.value?.brokeredCarrier?.type ===
    CapacityProviderType.CARRIER
    ? acceptedRateQuote.value?.carrier?.scac
    : undefined,
);

watch(
  [() => owningClient.value?.id, brokeredQuoteScac],
  ([clientId, scac]) => {
    if (!clientId) {
      brokerSetting.value = undefined;
      return;
    }

    loadAcceptedBrokerSettings(scac);
  },
  { immediate: true },
);
</script>
